import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import DocumentHeader, { DocumentFooter } from "@/components/DocumentHeader";
import SignatureCanvas from "@/components/SignatureCanvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Loader2, Download, AlertTriangle } from "lucide-react";

export default function PublicReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [signature, setSignature] = useState("");
  const [signerName, setSignerName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/public/reports/${token}`);
        setData(r.data);
        if (r.data.already_signed) setDone(true);
      } catch (e) { setErr(e?.response?.data?.detail || "No disponible"); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const sign = async () => {
    if (!signature) return toast.error("Firme en el recuadro");
    if (!signerName.trim()) return toast.error("Escriba su nombre");
    setSubmitting(true);
    try {
      const device = `${navigator.userAgent} | ${window.screen.width}x${window.screen.height}`;
      await axios.post(`${API_BASE}/public/reports/${token}/accept`, { signature, signer_name: signerName, device });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#005A91]" /></div>;
  if (err) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="bg-white border border-red-200 rounded-lg p-8 max-w-md text-center">
        <AlertTriangle className="h-12 w-12 mx-auto text-red-500 mb-3" />
        <div className="text-xl font-bold">Documento no disponible</div>
        <div className="text-sm text-slate-500 mt-2">{err}</div>
      </div>
    </div>
  );

  const { report: r, company } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      <DocumentHeader company={company} />

      <div className="max-w-5xl mx-auto px-4 lg:px-6 py-6 lg:py-8 pb-32">
        <div className="mb-6">
          <div className="text-[11px] font-black tracking-[0.24em] uppercase text-[#005A91]">Reporte de servicio</div>
          <h1 className="text-3xl lg:text-4xl font-black text-[#0A1118] tracking-tight font-numbers">{r.folio}</h1>
          <div className="text-slate-600 font-bold mt-1">{r.title}</div>
          {r.service_date && <div className="text-sm text-slate-500 mt-1">Fecha de servicio: {new Date(r.service_date).toLocaleDateString("es-MX")}</div>}
          {r.site && <div className="text-sm text-slate-500">Sitio: {r.site}</div>}
        </div>

        <div className="bg-white border border-[#005A91]/15 rounded-lg p-5 mb-6">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Cliente</div>
          <div className="text-lg font-bold text-[#0A1118]">{r.client?.name}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-slate-600 mt-2">
            {r.client?.rfc && <div><b>RFC:</b> {r.client.rfc}</div>}
            {r.client?.contact_name && <div><b>Contacto:</b> {r.client.contact_name}</div>}
            {r.client?.phone && <div><b>Tel:</b> {r.client.phone}</div>}
            {r.client?.address && <div className="sm:col-span-2"><b>Dirección:</b> {r.client.address}</div>}
          </div>
        </div>

        {r.equipment?.length > 0 && (
          <div className="bg-white border border-[#005A91]/15 rounded-lg overflow-hidden mb-6">
            <table className="w-full text-sm">
              <thead className="bg-[#005A91] text-white">
                <tr>
                  <th className="text-left py-2.5 px-3 text-[11px] font-black tracking-wider uppercase">#</th>
                  <th className="text-left py-2.5 px-3 text-[11px] font-black tracking-wider uppercase">Descripción</th>
                  <th className="text-center py-2.5 px-3 text-[11px] font-black tracking-wider uppercase">Unidad</th>
                  <th className="text-right py-2.5 px-3 text-[11px] font-black tracking-wider uppercase">Cant.</th>
                  <th className="text-left py-2.5 px-3 text-[11px] font-black tracking-wider uppercase">Modelo</th>
                  <th className="text-left py-2.5 px-3 text-[11px] font-black tracking-wider uppercase">N° Serie</th>
                </tr>
              </thead>
              <tbody>
                {r.equipment.map((e, i) => (
                  <tr key={e.id || i} className="border-b border-slate-100 last:border-0">
                    <td className="py-2.5 px-3 font-numbers text-slate-500">{i + 1}</td>
                    <td className="py-2.5 px-3">{e.description}</td>
                    <td className="py-2.5 px-3 text-center">{e.unit}</td>
                    <td className="py-2.5 px-3 text-right font-numbers">{e.quantity}</td>
                    <td className="py-2.5 px-3 font-numbers text-xs">{e.model}</td>
                    <td className="py-2.5 px-3 font-numbers text-xs">{e.serial}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {r.intro && (
          <div className="bg-white border border-[#005A91]/15 rounded-lg p-5 mb-6">
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Descripción del servicio</div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap">{r.intro}</div>
          </div>
        )}

        {r.photos?.length > 0 && (
          <div className="mb-6">
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 mb-3">Evidencia fotográfica</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {r.photos.sort((a, b) => (a.order || 0) - (b.order || 0)).map((p) => (
                <div key={p.id} className="bg-white border border-[#005A91]/15 rounded-lg overflow-hidden">
                  <img src={p.data_url} alt="" className="w-full h-56 object-cover bg-slate-100" />
                  {p.caption && <div className="p-3 text-sm text-slate-700 bg-slate-50">{p.caption}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {r.conclusion && (
          <div className="bg-white border border-[#005A91]/15 rounded-lg p-5 mb-6">
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Conclusiones</div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap">{r.conclusion}</div>
          </div>
        )}

        {r.technician_signature && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="bg-white border border-[#005A91]/15 rounded-lg p-4 text-center">
              <img src={r.technician_signature} alt="firma técnico" className="max-h-24 mx-auto mb-2" />
              <div className="border-t border-slate-300 pt-2 text-xs text-slate-500">Por parte de {company.name}</div>
              <div className="text-sm font-bold text-[#0A1118] mt-1">{r.technician_signature_name || "Técnico"}</div>
            </div>
            {done && r.client_signature ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
                <img src={r.client_signature} alt="firma cliente" className="max-h-24 mx-auto mb-2" />
                <div className="border-t border-emerald-300 pt-2 text-xs text-emerald-700">Firmado por el cliente</div>
                <div className="text-sm font-bold text-emerald-800 mt-1">{r.client_signature_name}</div>
              </div>
            ) : null}
          </div>
        )}

        {done ? (
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-6 text-center mb-6" data-testid="report-signed-state">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600 mb-2" />
            <div className="text-xl font-black text-emerald-700">Reporte firmado</div>
            <div className="text-sm text-emerald-600 mt-2">Gracias por confirmar la conformidad del servicio.</div>
          </div>
        ) : (
          <div className="bg-white border-2 border-[#005A91] rounded-lg p-5 lg:p-6 mb-6" data-testid="sign-panel">
            <div className="text-center mb-4">
              <ShieldCheck className="h-10 w-10 mx-auto text-[#4ECCC8] mb-2" />
              <div className="text-lg font-black text-[#005A91]">Firme su conformidad</div>
              <div className="text-xs text-slate-500 mt-1">Su firma quedará registrada con fecha, IP y dispositivo.</div>
            </div>
            <div className="mb-3">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Nombre completo</Label>
              <Input data-testid="signer-name-input" value={signerName} onChange={(e) => setSignerName(e.target.value)} className="mt-1 h-11 border-[#005A91]/20" placeholder="Ej: Ing. Juan Pérez" />
            </div>
            <div className="mb-4">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Firma</Label>
              <div className="mt-1">
                <SignatureCanvas value={signature} onChange={setSignature} height={200} />
              </div>
            </div>
            <Button onClick={sign} disabled={submitting} data-testid="sign-btn" className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 font-bold text-base">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-5 w-5 mr-2" />}
              Aceptar y firmar conformidad
            </Button>
          </div>
        )}

        <div className="text-center">
          <Button variant="outline" onClick={() => window.open(`${API_BASE}/public/reports/${token}/pdf`, "_blank")} className="border-[#005A91]/30 text-[#005A91]" data-testid="report-download-pdf-btn">
            <Download className="h-4 w-4 mr-2" /> Descargar PDF
          </Button>
        </div>
      </div>

      <DocumentFooter company={company} />
    </div>
  );
}
